import { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../lib/stripe.js';
import { savePaymentMethod } from '../api/client.js';
import { ModalOverlay } from '../components/ModalOverlay.js';
import type { SavedCard } from '@aslgo/shared';

function SetupForm({ onSaved, onClose }: { onSaved: (card: SavedCard | null) => void; onClose: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    if (error) {
      setErr(error.message ?? 'Could not save card.');
      setBusy(false);
      return;
    }
    try {
      const res = await savePaymentMethod(setupIntent!.id);
      onSaved(res.card);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement options={{ wallets: { link: 'never' } }} />
      {err ? <div className="notice-error">{err}</div> : null}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={() => { void submit(); }} disabled={busy || !stripe}>
          {busy ? 'Saving…' : 'Save card'}
        </button>
      </div>
    </div>
  );
}

/** Setup-Element modal for adding/updating the learner's card on file. `clientSecret`
 *  comes from a freshly-created SetupIntent (see LearnerBilling in ProfileView.tsx).
 *  No PAN ever touches our code — PaymentElement renders Stripe's iframe, and we only
 *  ever pass the resulting setupIntentId to the backend. */
export function CardOnFileModal({ clientSecret, onSaved, onClose }: { clientSecret: string; onSaved: (card: SavedCard | null) => void; onClose: () => void }) {
  return (
    <ModalOverlay
      role="dialog"
      aria-modal="true"
      aria-label="Save a card"
      className="z-50 bg-ink-900/60"
    >
      <div className="max-w-sm w-[90%] mx-auto rounded-2xl bg-canvas-light dark:bg-canvas-dark p-6 shadow-xl ring-1 ring-ink-900/10 dark:ring-white/10">
        <h2 className="h-card text-ink-900 dark:text-ink-100 mb-4">Card on file</h2>
        <Elements stripe={getStripe()} options={{ clientSecret }}>
          <SetupForm onSaved={onSaved} onClose={onClose} />
        </Elements>
      </div>
    </ModalOverlay>
  );
}
